import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery, createApiErrorTransformer } from '@/lib/api/baseQuery';
import { EquityOverviewAPIResponse, getHoldingDataAPIResponse } from '@/types/wealth/wealth';
import { SectorWisePortfolioApiResponse } from '@/types/wealth/sectorWisePortfolio';
import {
  BenchmarksApiSuccessResponse,
  BenchmarksTransformedData,
  BenchmarkTimeframeKey,
  GetBenchmarksDataParams,
  NormalizedBenchmarkItem,
} from '@/types/wealth/benchmarks';
import { EquityPerformanceApiResponse } from '@/types/wealth/equityPerformance';
import { SectorWisePerformanceApiResponse } from '@/types/wealth/sectorWisePerformance';
import { OrderHistoryApiResponse } from '@/types/wealth/orderHistory';
import { WealthAnalysisApiResponse, WealthAnalysisParams } from '@/types/wealth/wealthAnalysis';
const WEALTH_API_URL = import.meta.env.VITE_CONNECT_URL;

export const wealthApi = createApi({
  reducerPath: 'wealthApi',
  // Using connect2 base service; adjust if dedicated wealth key added later
  baseQuery: createBaseQuery(WEALTH_API_URL),
  tagTypes: ['Dashboard'],
  endpoints: (builder) => ({
    // New: Branch Equity Overview (pre-search aggregate)
    getBranchEquityOverview: builder.query<
      EquityOverviewAPIResponse,
      { source?: string; financialYear?: number | string; isHardRefresh?: boolean } | void
    >({
      query: ({ source = 'ap', financialYear = '2025', isHardRefresh = false } = {}) => ({
        url: `/v2/wealth/get-branch-equity-overview`,
        method: 'GET',
        params: {
          source,
          financialYear,
          ...(isHardRefresh ? { isHardRefresh: true } : {}),
        },
      }),
      providesTags: () => ['Dashboard'],
      transformErrorResponse: createApiErrorTransformer('WealthBranchEquityOverview', false),
    }),
    // Existing: Equity Overview
    getWealthAnalysis: builder.query<WealthAnalysisApiResponse, WealthAnalysisParams | void>({
      query: ({
        source = 'ap',
        financialYear = '2025',
        range = 'cy',
        isHardRefresh = false,
        clientCode,
      } = {}) => ({
        url: `/v2/wealth/get-wealth-analysis-data`,
        method: 'GET',
        params: {
          source,
          financialYear,
          range,
          ...(isHardRefresh ? { isHardRefresh: true } : {}),
          // include clientCode only when provided
          ...(clientCode ? { clientCode } : {}),
        },
      }),
      providesTags: () => ['Dashboard'],
      transformResponse: (response: WealthAnalysisApiResponse) => {
        console.log('📊 Wealth Analysis API Response:', response);
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('WealthAnalysis', false),
    }),

    // Existing: Equity Overview
    getEquityOverview: builder.query<
      EquityOverviewAPIResponse,
      { source: string; financialYear: number; isHardRefresh?: boolean; clientCode?: string } | void
    >({
      query: (params) => ({
        url: `/v2/wealth/get-equity-overview`,
        method: 'GET',
        params: {
          source: 'ap',
          financialYear: params?.financialYear,
          isHardRefresh: params?.isHardRefresh ? true : undefined,
          ...(params?.clientCode ? { clientCode: params.clientCode } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('WealthEquityOverview', false),
    }),

    // Moved: Benchmarks Data
    getBenchmarksData: builder.query<BenchmarksTransformedData, GetBenchmarksDataParams | void>({
      query: ({
        financialYear = '2025',
        range = '1m,2m,3m,6m,9m,1y,3y,cy',
        source = 'ap',
        isHardRefresh = false,
        benchmarks,
        clientCode,
      } = {}) => ({
        url: `/v2/wealth/get-benchmarks-data`,
        method: 'POST',
        params: {
          financialYear,
          range,
          source,
          clientCode,
          // Only send when truthy to avoid breaking server expectations
          ...(isHardRefresh ? { isHardRefresh: true } : {}),
        },
        body: {
          benchmarks,
        },
      }),
      providesTags: () => ['Dashboard'],
      transformResponse: (response: BenchmarksApiSuccessResponse): BenchmarksTransformedData => {
        const raw = response?.data || {};
        const defaultRanges: BenchmarkTimeframeKey[] = [
          '1m',
          '2m',
          '3m',
          '6m',
          '9m',
          '1y',
          '3y',
          'cy',
        ];
        const firstKey = Object.keys(raw)[0];
        const ranges: BenchmarkTimeframeKey[] = firstKey
          ? (Object.keys((raw as any)[firstKey]) as BenchmarkTimeframeKey[])
          : defaultRanges;

        const benchmarks: NormalizedBenchmarkItem[] = Object.entries(raw).map(([name, map]) => ({
          name,
          points: (ranges as BenchmarkTimeframeKey[]).map((r) => ({
            range: r,
            value: (map as any)[r] ?? 0,
          })),
          map: map as any,
        }));

        const byName: Record<string, NormalizedBenchmarkItem> = {};
        benchmarks.forEach((b) => (byName[b.name] = b));

        return { benchmarks, ranges, byName };
      },
      transformErrorResponse: createApiErrorTransformer('DashboardBenchmarks', false),
    }),

    // New: Equity Month-wise data (for chart + heatmap)
    getEquityMonthWise: builder.query<
      any,
      {
        financialYear?: string | number;
        source?: string;
        keyFormat?: number;
        clientCode?: string;
      } | void
    >({
      query: ({ financialYear = '2025', source = 'ap', keyFormat = 2, clientCode } = {}) => ({
        url: `/v2/wealth/get-equity-month-wise`,
        params: { financialYear, source, keyFormat, ...(clientCode ? { clientCode } : {}) },
        method: 'GET',
      }),
      providesTags: () => ['Dashboard'],
      transformErrorResponse: createApiErrorTransformer('WealthEquityMonthWise', false),
      transformResponse: (response: any) => {
        // Keep raw response; consumers will normalize for charts/heatmap
        return response;
      },
    }),
    // Moved: Sector Wise Portfolio
    getSectorWisePortfolio: builder.query<
      SectorWisePortfolioApiResponse,
      { financialYear?: string; source?: string }
    >({
      query: ({ financialYear = '2025', source = 'ap' } = {}) => ({
        url: `/v2/wealth/get-sector-wise-portfolio`,
        params: { financialYear, source },
      }),
      providesTags: () => ['Dashboard'],
      transformResponse: (response: any) => {
        console.log('📊 Sector Wise Portfolio API Response:', response);
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('WealthSectorWisePortfolio', false),
    }),

    // New: Order History (Short/Long/Trading + Expenses)
    getOrderHistory: builder.query<
      OrderHistoryApiResponse,
      { financialYear?: string | number; source?: string; clientCode?: string } | void
    >({
      query: ({ financialYear = '2025', source = 'ap', clientCode } = {}) => ({
        url: `/v2/wealth/get-order-history`,
        method: 'GET',
        params: { financialYear, source, clientCode },
      }),
      providesTags: () => ['Dashboard'],
      transformErrorResponse: createApiErrorTransformer('WealthOrderHistory', false),
    }),
  }),
});

export const {
  useGetBranchEquityOverviewQuery,
  useGetWealthAnalysisQuery,
  useLazyGetWealthAnalysisQuery,
  useGetEquityOverviewQuery,
  useGetSectorWisePortfolioQuery,
  useGetBenchmarksDataQuery,
  useLazyGetBenchmarksDataQuery,
  useGetEquityMonthWiseQuery,
  useGetOrderHistoryQuery,
} = wealthApi;
